import './init.scss'
import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import globalAppUserStore, { APIStatusType } from '../stores/globalAppUserStore';
import AppUserManager from '../managers/AppUserManager';
import { useHookstate } from '@hookstate/core';
import globalAuthStateStore from '../stores/globalAuthStateStore';
import Loading from '../components/Loading';
import InitLayout from './layout/InitLayout';
import TOS from './TOS';
import ButtonControl from './controls/ButtonControl';

const Init = () => {
  const location = useLocation();
  const globalAuthState = useHookstate(globalAuthStateStore);
  const authState = globalAuthState.get();
  const globalAppUserState = useHookstate(globalAppUserStore);
  const getAppUserState = globalAppUserState.get();
  const appUserManager = useMemo(() => new AppUserManager(globalAuthState, globalAppUserState), [globalAuthState, globalAppUserState]);
  const [initUserPhase, setInitUserPhase] = useState(0);
  const { apiStatus } = getAppUserState;

  useEffect(() => {
    if (location?.pathname &&
      !location?.pathname?.toLowerCase()?.includes('admin/content/edit') &&
      !location?.pathname?.toLowerCase()?.includes('admin/content/addnew')
    ) {
      sessionStorage.removeItem('FileUpload-content');
      sessionStorage.removeItem('FileUpload-displayContent');
      sessionStorage.removeItem('screen-data');
    }
  }, [location?.pathname]);

  useEffect(() => {
    if (APIStatusType.UserSetup == apiStatus && initUserPhase === 0) {
      setInitUserPhase(1);
      setTimeout(() => { setInitUserPhase(2); }, 3000);
    }
    else if (APIStatusType.TOSNotFound == apiStatus && initUserPhase === 0) {
      setInitUserPhase(2);
    }
  }, [apiStatus]);

  useEffect(() => {
    appUserManager.initialize();
  }, [authState, appUserManager]);

  const onSubmitNewAccount = (id) => {
    setInitUserPhase(4);
    appUserManager.setupUserProfile(id).then((r) => {
      window.location.href = '/profile';
    }).catch(e => {
      setInitUserPhase(999);
    });
  }


  const onSubmitTOS = (id) => {
    setInitUserPhase(5);
    appUserManager.updateTOS(id).then((r) => {
      window.location.href = appUserManager.defaultRoute();
    }).catch(e => {
      setInitUserPhase(999);
    });
  }

  const onCancelTOS = () => {
    setInitUserPhase(3);
  }

  let render = null;
  switch (apiStatus) {
    case APIStatusType.Startup:

      render = <InitLayout><div className='init__content'>&nbsp;</div></InitLayout>;
      break;
    case APIStatusType.AccessDenied:
      render = <InitLayout><div className='init__content'><h3>Your account is not active.</h3><h4><i>This can be caused by a deactivation of your user, school or district. Please contact Customer Support for more information.</i></h4></div></InitLayout>;
      break;
    case APIStatusType.InitFailed:
    case APIStatusType.Offline:
      render = <InitLayout><div className='init__content'><div className='init__content-container'><h4>You may be noticing slower load times. To ensure the best performance, please review the steps below:</h4><h4><strong>Check your connection</strong><br />Visit <a href="https://www.speedtest.net" target="_blank" rel="noopener noreferrer">speedtest.net</a> and click <strong>Go</strong> to run a speed test. We recommend at least 20 Mbps download and 5 Mbps upload speeds.</h4><h4><strong>Optimize your browser</strong><br />Enable pop-ups, close unused tabs and applications, and refresh your browser. Google Chrome is recommended.</h4><h4><strong>Stay on one network</strong><br />Avoid switching between Wi-Fi and a hotspot during your session. Once you begin your session, remain connected to a stable network.</h4><h4>If the issue persists, contact <a href="mailto:support@niet.org">support@niet.org</a> for assistance.</h4></div></div></InitLayout>;
      break;
    case APIStatusType.Initializing:
      render = <InitLayout><div className='init__content'><Loading type='default' size='5rem' /><h5>Initializing...</h5></div></InitLayout>;
      break;
    case APIStatusType.OnlineSecure:
      render = <InitLayout><div className='init__content'><Loading type='default' size='5rem' /><h5>Initializing...</h5></div></InitLayout>;
      break;
    case APIStatusType.TOSNotFound:
      if (initUserPhase === 2) {
        render = <InitLayout><div className='init__content'><TOS onSubmit={onSubmitTOS} onCancel={onCancelTOS} /></div></InitLayout>;
        break;
      }
      else if (initUserPhase === 3) {
        render = <InitLayout><div className='init__content'><h4>You have rejected the terms of service. Please contact customer support if you have any questions or need additional information.</h4><ButtonControl onClick={() => { setInitUserPhase(2) }}>Review Terms</ButtonControl></div></InitLayout>;
        break;
      }
      else if (initUserPhase === 5) {
        render = <InitLayout><div className='init__content'><Loading type='default' size='5rem' /><h5>Updating your Terms of Service...</h5></div></InitLayout>;
        break;
      }
    case APIStatusType.UserSetup:
      if (initUserPhase === 1) {
        render = <InitLayout><div className='init__content'><Loading type='default' size='5rem' /><h5>Welcome {authState?.idTokenClaims?.name}! Registering your account...</h5></div></InitLayout>;
        break;
      }
      else if (initUserPhase === 2) {
        render = <InitLayout><div className='init__content'><TOS onSubmit={onSubmitNewAccount} onCancel={onCancelTOS} /></div></InitLayout>;
        break;
      }
      else if (initUserPhase === 3) {
        render = <InitLayout><div className='init__content'><h4>You have rejected the terms of service. Please contact customer support if you have any questions or need additional information.</h4><ButtonControl onClick={() => { setInitUserPhase(2) }}>Review Terms</ButtonControl></div></InitLayout>;
        break;
      }
      else if (initUserPhase === 4) {
        render = <InitLayout><div className='init__content'><Loading type='default' size='5rem' /><h5>Finalizing your registration...</h5></div></InitLayout>;
        break;
      }
      else if (initUserPhase === 999) {
        render = <InitLayout><div className='init__content'><h4>An error has occurred while registering your account. Please contact customer support.</h4></div></InitLayout>;
        break;
      }
    default:
      render = null;
      break;
  }

  const handleRender = () => {
    if (apiStatus === APIStatusType.OnlineSecure && !getAppUserState.loadingData) {
      return
    } else {

      return <div className="init__blockUi">{render}</div>
    }
  }

  return (<>
    {(apiStatus === APIStatusType.Initializing || apiStatus === APIStatusType.OnlineSecure || apiStatus === APIStatusType.Startup || apiStatus === APIStatusType.Offline ||
      apiStatus === APIStatusType.InitFailed || apiStatus === APIStatusType.AccessDenied || apiStatus === APIStatusType.UserSetup || apiStatus === APIStatusType.TOSNotFound) && <>{handleRender()}</>}
  </>);
}

export default Init;